/**
 * Meta Pixel helper — thin, safe wrapper around `window.fbq` so callers
 * don't need to guard for missing pixel / ad-blockers / dev-mode.
 *
 * Standard events documented at:
 *   https://developers.facebook.com/docs/meta-pixel/reference
 *
 * All helpers are fire-and-forget. They never throw and never block the UX
 * (used inside checkout / RSVP handlers where a tracking failure must
 * absolutely not break the primary flow).
 */

const isBrowserPixelReady = () =>
  typeof window !== 'undefined' && typeof window.fbq === 'function';

function safeTrack(eventName, params) {
  try {
    if (isBrowserPixelReady()) {
      if (params) {
        window.fbq('track', eventName, params);
      } else {
        window.fbq('track', eventName);
      }
    }
  } catch {
    /* fbq missing / blocked / init race — silent no-op */
  }
}

/** Fires when a user completes a paid ticket purchase.
 *  Meta uses this to optimize ad delivery toward high-intent buyers and
 *  attributes revenue back to specific campaigns. */
export function trackPurchase({ value, currency = 'INR', contentIds, contentName, numItems }) {
  const params = {
    value: Number(value ?? 0),
    currency,
    ...(contentIds ? { content_ids: contentIds, content_type: 'product' } : {}),
    ...(contentName ? { content_name: contentName } : {}),
    ...(numItems ? { num_items: Number(numItems) } : {}),
  };
  safeTrack('Purchase', params);
}

/** Fires when a guest submits an RSVP (guest-list / waitlist).
 *  Lead is Meta's canonical "user showed high intent but didn't pay" event —
 *  perfect for guest-list flows where the guest converts without a card charge. */
export function trackLead({ contentName, contentCategory, status } = {}) {
  const params = {
    ...(contentName ? { content_name: contentName } : {}),
    ...(contentCategory ? { content_category: contentCategory } : {}),
    // Meta ignores unknown keys, but keeping status lets us slice in the
    // dashboard's custom-conversions builder ("only confirmed" vs "waitlist").
    ...(status ? { status } : {}),
  };
  safeTrack('Lead', params);
}

/** Fires when a user starts a paid checkout (e.g. taps "Confirm Purchase"). */
export function trackInitiateCheckout({ value, currency = 'INR', numItems, contentName } = {}) {
  const params = {
    ...(value != null ? { value: Number(value), currency } : {}),
    ...(numItems ? { num_items: Number(numItems) } : {}),
    ...(contentName ? { content_name: contentName } : {}),
  };
  safeTrack('InitiateCheckout', params);
}

/** Fires when a user creates a Unight account. */
export function trackCompleteRegistration({ method, contentName } = {}) {
  const params = {
    ...(method ? { registration_method: method } : {}),
    ...(contentName ? { content_name: contentName } : {}),
  };
  safeTrack('CompleteRegistration', params);
}

/** Fires when a user views a specific event detail page — Meta's "product view"
 *  equivalent, powers retargeting audiences ("people who viewed but didn't buy"). */
export function trackViewContent({ contentIds, contentName, contentCategory, value, currency = 'INR' } = {}) {
  const params = {
    ...(contentIds ? { content_ids: contentIds, content_type: 'product' } : {}),
    ...(contentName ? { content_name: contentName } : {}),
    ...(contentCategory ? { content_category: contentCategory } : {}),
    ...(value != null ? { value: Number(value), currency } : {}),
  };
  safeTrack('ViewContent', params);
}
